/**
 * Datenhelfer des Kapitels „Umsatz & Bezahlqualität" für den Persona-Umbau C (28.10.2026, Plan
 * `.claude/plans/2026-10-28-persona-umbau-paket-1-2.md` § C). Die Grundtypen und Abrufe liegen in `auswertungenData.ts`;
 * hier stehen nur die neuen Felder der Antworten und GETEILTE Abrufe: Kopfkacheln und Karten lesen dieselbe Antwort
 * (eine Anfrage je Seitenaufruf, Muster `fetchPrognoseGeteilt`) — damit stehen in Kopf und Karte dieselben Zahlen.
 */
import {
  fetchKennzahlenP2,
  fetchKundentypP2,
  fetchZahlungseingang,
  KennzahlenAntwortP2,
  KundentypAntwortP2,
  QualitaetZeileP2,
  Veraenderung,
  ZahlungseingangAntwort,
} from './auswertungenData';
import { fetchData } from './apiUtils';

/** Ein Bestellquartal mit Veränderung gegen das Vorjahresquartal (`umsatzService.quartalsVeraenderungen`). */
export interface QuartalVerlauf {
  quarter: string;
  /** Am Datenstand geschnitten, `YYYY-MM-DD HH:mm:ss`, `bis` exklusiv. */
  fenster: { von: string; bis: string };
  veraenderung: Veraenderung | null;
  laufend: boolean;
  /** Anfos ohne vorläufige. */
  anfos: number;
  value_sum: number;
}

export interface KennzahlenAntwortC extends KennzahlenAntwortP2 {
  /** Vorjahr und laufendes Jahr, je Quartal (Persona-Umbau C1/C2). Fehlt bei einem älteren Backend. */
  quartale_verlauf?: QuartalVerlauf[];
}

/** Neue Felder je Quartal/Monat der Rechnungsstufen (C3 Euro je Stufe, C4 Bindung). */
export interface StufenZusatz {
  value_sum?: number;
  value_paid?: number;
  value_trial_only?: number;
  value_invoiced_unpaid?: number;
  value_no_invoice_running?: number;
  value_no_invoice_ended?: number;
  laeuft_zaehler?: number;
  laeuft_nach_6_monaten?: number | null;
  youngest_age_days?: number | null;
}
export type QualitaetZeileC = QualitaetZeileP2 & StufenZusatz & { months?: QualitaetZeileC[] };

/** Eine Anfrage je Seitenaufruf, 60 s geteilt; ein Fehler gibt den Platz frei. */
function geteilt<T>(holen: () => Promise<T>): () => Promise<T> {
  let platz: { at: number; p: Promise<T> } | null = null;
  return () => {
    if (!platz || Date.now() - platz.at > 60_000) {
      const p = holen();
      platz = { at: Date.now(), p };
      p.catch(() => {
        platz = null;
      });
    }
    return platz.p;
  };
}

export const fetchKennzahlenGeteilt = geteilt(() => fetchKennzahlenP2() as Promise<KennzahlenAntwortC>);
export const fetchKundentypGeteilt = geteilt<KundentypAntwortP2>(() => fetchKundentypP2());
export const fetchZahlungseingangGeteilt = geteilt<ZahlungseingangAntwort>(() => fetchZahlungseingang());

// --- Paket M (Workshop-Lücken 29.10.2026): Bindungskurve (A1) und Prüfstand (A3) der Anfo-Wert-Seite. Eigene Endpunkte
// mit eigenem Cache (`bindungService.ts`, `pruefstandService.ts`), damit die Seite nicht auf die Modell-Lader wartet.

/** Antwort `{success, data}` auspacken; Fehler werfen (gemeinsam für die Workshop-Karten, Pakete M und U). */
async function hole<T>(url: string): Promise<T> {
  const res = await fetchData<T>(url);
  if (!res.success || res.data === undefined) throw new Error(res.error || 'Laden fehlgeschlagen');
  return res.data;
}

export interface BindungPunkt {
  /** Rechnung Nr. (1 = erste bezahlte Rechnung). */
  rechnung: number;
  /** Modell (sBG des wirksamen Laufs): Anteil der Zahler, die diese Rechnung bezahlen. */
  modell: number;
  /** Gemessen mit heutigem Stand (Kaplan-Meier); null, wenn niemand so weit beobachtet ist. */
  gemessen: number | null;
  im_risiko: number | null;
  gekuendigt: number | null;
  /** Unter `min_im_risiko` Zahlern im Risiko. */
  grau: boolean;
}

export interface BindungTyp {
  typ: 'Q' | 'U';
  a: number;
  b: number;
  n_lauf: number;
  zahler_heute: number;
  gekuendigt_heute: number;
  offen_heute: number;
  punkte: BindungPunkt[];
}

export interface BindungAntwort {
  as_of: string;
  run: { id: number; as_of: string; mode: string; model_version: string } | null;
  min_im_risiko: number;
  typen: BindungTyp[] | null;
}

export interface PruefstandAntwort {
  as_of: string;
  abgenommen: false;
  erster_m5_lauf: string | null;
  termine: { key: 'tag75' | 'monate6' | 'monate12'; ab: string; erreicht: boolean }[];
  snapshot_ab: string;
}

export const fetchBindung = () => hole<BindungAntwort>('/api/auswertungen/umsatz/anfo-wert/bindung');
export const fetchPruefstand = () => hole<PruefstandAntwort>('/api/auswertungen/umsatz/anfo-wert/pruefstand');
// --- /Paket M ---

// --- Workshop-Lücken 29.10.2026, Paket U (Plan .claude/plans/2026-10-29-workshop-luecken.md) ---
// W1 Feld `abbruch_tage` an /bezahlqualitaet; N1 /verlust-bilanz, A4 /geld-nach-alter, N3 /qualitaet-je-quelle?dim=.

/** W1: Tag des Endes der vor der ersten Rechnung beendeten Anfos je Bestellquartal (`abbruchTage.ts`). */
export interface AbbruchQuartal {
  quarter: string;
  reif: boolean;
  n: number;
  ohne_datum: number;
  p25: number | null;
  median: number | null;
  p75: number | null;
  p90: number | null;
  max: number | null;
  histogramm: { tag: number; n: number }[];
  testphase: { tage: number | null; n: number }[];
  testphase_30_anteil: number | null;
}
export interface AbbruchTage {
  quartale: AbbruchQuartal[];
  testphase_linie: number | null;
  testphase_tage: number;
  testphase_linie_anteil: number;
}

/** N1 Verlust-Bilanz. */
export type VerlustStufeKey = 'no_invoice_running' | 'no_invoice_ended' | 'invoiced_unpaid' | 'trial_only' | 'paid';
export interface Bilanz {
  von: string;
  bis: string;
  bestellungen: number;
  verweigert: number;
  verweigert_anteil: number | null;
  anfos: number;
  vorlaeufig: number;
  bekannt: number;
  stufen: { key: VerlustStufeKey; n: number; anteil: number | null }[];
  summe_ok: boolean;
  /** null = am Stichtag kein Anfo-Wert-Modell (Anzeige „—“). */
  erwarteter_wert: number | null;
  bisher_bezahlt: number;
}
export interface VerlustBilanzAntwort {
  as_of: string;
  datenstand: string | null;
  reife_tage: number;
  jetzt: Bilanz;
  vorjahr: Bilanz;
}

/** A4 Geld nach Alter. */
export interface GeldZelle {
  h: number;
  wert: number | null;
  n: number;
  N: number;
  ueber_cpa: boolean | null;
}
export interface GeldMonat {
  month: string;
  anfos: number;
  cpa_je_anfo: number | null;
  mit_cpa: number;
  ohne_cpa: number;
  zellen: GeldZelle[];
  cpa_erreicht_ab: { h: number; spaetestens: boolean } | null;
}
export interface GeldNachAlterAntwort {
  as_of: string;
  snapshot_stand: string | null;
  snapshot_ab: string;
  fehlende_tage: string[];
  alter: number[];
  rows: GeldMonat[];
  unbeobachtbar_status: number;
}

/** N3 Anfo-Qualität je Quelle. */
export type QualitaetDimension = 'produkt' | 'kanalgruppe' | 'webinar';
export interface QualitaetQuelleZeile {
  key: string;
  label: string;
  zusatz: string | null;
  n: number;
  paid: number;
  pay_rate: number | null;
  n_basis: number;
  paid_basis: number;
  pay_rate_basis: number | null;
  delta_pp: number | null;
  wenig: boolean;
  ohne_vergleich: 'kein_etikett' | null;
  test: { z: number | null; p: number; p_holm: number | null; flag: 'up' | 'down' | null } | null;
}
export interface QualitaetQuelleAntwort {
  as_of: string;
  dim: QualitaetDimension;
  datenstand: string | null;
  fenster: { jetzt: { von: string; bis: string }; vorjahr: { von: string; bis: string } };
  reife_tage: number;
  schwelle: number;
  alpha: number;
  vergleich: 'vorjahr' | 'produkt_rest';
  alle: QualitaetQuelleZeile;
  rows: QualitaetQuelleZeile[];
  familie: number;
}

const UM_U = '/api/auswertungen/umsatz';
export const fetchVerlustBilanz = () => hole<VerlustBilanzAntwort>(`${UM_U}/verlust-bilanz`);
export const fetchGeldNachAlter = () => hole<GeldNachAlterAntwort>(`${UM_U}/geld-nach-alter`);
export const fetchQualitaetQuelle = (dim: QualitaetDimension) => hole<QualitaetQuelleAntwort>(`${UM_U}/qualitaet-je-quelle?dim=${dim}`);
